import React, { useState, useEffect } from 'react';
import { ArrowLeft, Upload, Settings, Image, Calendar, User, Globe, Lock, X, ChevronLeft, ChevronRight, Trash2, Download, Star, RefreshCw, Loader, Play, Info } from 'lucide-react';
import { createPortal } from 'react-dom';
import './AlbumViewer.css';
import { getApiUrl } from '../utils/config';
import authService from '../services/authService';
import MobilePhotoViewer from './MobilePhotoViewer';

const AlbumViewer = ({ album, onBack, onUpload, canUpload, onDeleteAlbum }) => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [user, setUser] = useState(null);
  const [currentCoverPhotoId, setCurrentCoverPhotoId] = useState(album?.cover_photo_id);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [photosPerPage] = useState(() => {
    // Determine photos per page based on device capabilities
    const isMobile = window.innerWidth <= 768;
    const isLowEndDevice = navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 4;
    
    if (isMobile && isLowEndDevice) {
      return 15; // Very conservative for low-end mobile devices
    } else if (isMobile) {
      return 25; // Moderate for mobile
    } else {
      return 50; // Desktop can handle more
    }
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showMobileViewer, setShowMobileViewer] = useState(false);

  useEffect(() => {
    // Get current user
    checkAuthStatus();
  }, []);

  useEffect(() => {
    if (album) {
      fetchAlbumPhotos();
      setCurrentCoverPhotoId(album.cover_photo_id);
    }
  }, [album]);

  // Add useEffect to handle external refresh triggers
  useEffect(() => {
    if (album && lastRefresh) {
      fetchAlbumPhotos();
    }
  }, [lastRefresh]);

  // Cleanup function to restore body scroll on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const checkAuthStatus = async () => {
    try {
      const response = await fetch(`${getApiUrl()}/auth/status`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.authenticated) {
        setUser(data.user);
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
    }
  };

  // Enhanced fetchAlbumPhotos with retry logic
  const fetchAlbumPhotos = async (retryCount = 0) => {
    setLoading(true);
    try {
      const response = await authService.authenticatedFetch(`${getApiUrl()}/api/photos/album/${album.id}`);
      if (response.ok) {
        const data = await response.json();
        const newPhotos = data.photos || [];
        
        // If we got no photos and this is after an upload, retry once after a delay
        if (newPhotos.length === 0 && retryCount === 0 && lastRefresh) {
          console.log('No photos found, retrying in 3 seconds...');
          setTimeout(() => {
            fetchAlbumPhotos(1);
          }, 3000);
          return;
        }
        
        setPhotos(newPhotos);
        console.log(`Loaded ${newPhotos.length} photos for album ${album.id}`);
        
        // Calculate pagination
        const calculatedTotalPages = Math.ceil(newPhotos.length / photosPerPage);
        setTotalPages(calculatedTotalPages);
        console.log(`📊 Album has ${newPhotos.length} photos, showing ${photosPerPage} per page (${calculatedTotalPages} pages)`);
        
        // Debug: Log content types to help identify video issues
        const videoCount = newPhotos.filter(photo => photo.content_type?.startsWith('video/')).length;
        console.log(`📹 Found ${videoCount} videos in album`);
      } else {
        console.error('Failed to fetch album photos:', response.status);
      }
    } catch (error) {
      console.error('Error fetching album photos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSetCoverPhoto = async (photoId, event) => {
    event.stopPropagation();
    
    try {
      const response = await authService.authenticatedFetch(`${getApiUrl()}/api/albums/${album.id}/cover-photo`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ photo_id: photoId })
      });

      if (response.ok) {
        setCurrentCoverPhotoId(photoId);
        alert('Cover photo updated successfully!');
      } else {
        alert('Failed to set cover photo');
      }
    } catch (error) {
      console.error('Error setting cover photo:', error);
      alert('Error setting cover photo');
    }
  };

  const handleDeletePhoto = async (photoId, event) => {
    event.stopPropagation();
    
    if (!window.confirm('Are you sure you want to delete this photo?')) {
      return;
    }

    try {
      const response = await authService.authenticatedFetch(`${getApiUrl()}/api/photos/${photoId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setPhotos(photos.filter(p => p.id !== photoId));
        
        if (selectedPhoto && selectedPhoto.id === photoId) {
          closeLightbox();
        }
        
        // If we deleted the cover photo, clear it
        if (currentCoverPhotoId === photoId) {
          setCurrentCoverPhotoId(null);
        }
      } else {
        alert('Failed to delete photo');
      }
    } catch (error) {
      console.error('Error deleting photo:', error);
      alert('Error deleting photo');
    }
  };

  const handleDeleteAlbum = async () => {
    setDeleting(true);
    try {
      const response = await authService.authenticatedFetch(`${getApiUrl()}/api/albums/${album.id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        const result = await response.json();
        console.log(result.message);
        
        // Call parent's onDeleteAlbum callback if provided
        if (onDeleteAlbum) {
          onDeleteAlbum(album.id);
        }
        
        // Navigate back to gallery
        onBack();
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to delete album');
      }
    } catch (error) {
      console.error('Error deleting album:', error);
      alert('Error deleting album');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const canDeletePhoto = (photo) => {
    if (!user) return false;
    return photo.user_id === user.id || album.user_id === user.id;
  };

  const isAlbumOwner = () => {
    return user && album && album.user_id === user.id;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Manual refresh function
  const refreshAlbumPhotos = async () => {
    setRefreshing(true);
    try {
      console.log('Manually refreshing album photos...');
      await fetchAlbumPhotos();
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error refreshing photos:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const openLightbox = (photoIndex) => {
    setCurrentPhotoIndex(photoIndex);
    setSelectedPhoto(photos[photoIndex]);
    
    // Use mobile viewer on mobile devices, regular lightbox on desktop
    if (isMobile) {
      setShowMobileViewer(true);
    } else {
      // Desktop - prevent body scroll
      document.body.style.overflow = 'hidden';
    }
  };

  const closeLightbox = () => {
    setSelectedPhoto(null);
    setShowMobileViewer(false);
    document.body.style.overflow = '';
  };

  const navigatePhoto = (direction) => {
    let newIndex;
    if (direction === 'next') {
      newIndex = currentPhotoIndex < photos.length - 1 ? currentPhotoIndex + 1 : 0;
    } else {
      newIndex = currentPhotoIndex > 0 ? currentPhotoIndex - 1 : photos.length - 1;
    }
    setCurrentPhotoIndex(newIndex);
    setSelectedPhoto(photos[newIndex]);
  };

  if (!album) {
    return <div>Album not found</div>;
  }

  return (
    <div className="album-viewer">
      <header className="album-header">
        <div className="album-header-left">
          <button className="btn-back" onClick={onBack}>
            <ArrowLeft size={20} />
            Back to Gallery
          </button>
          
          <div className="album-title-section">
            <h1>{album.name}</h1>
            {album.description && <p className="album-description">{album.description}</p>}
            
            <div className="album-meta">
              <span className="album-author">
                <User size={16} />
                {album.username}
              </span>
              
              <span className="album-date">
                <Calendar size={16} />
                Updated {formatDate(album.updated_at)}
              </span>
              
              <span className={`privacy-badge ${album.is_public ? 'public' : 'private'}`}>
                {album.is_public ? <Globe size={16} /> : <Lock size={16} />}
                {album.is_public ? 'Public' : 'Private'}
              </span>
              
              <span className="photo-count">
                <Image size={16} />
                {photos.length} {photos.length === 1 ? 'item' : 'items'}
              </span>
            </div>
          </div>
        </div>
        
        <div className="album-header-right">
          {canUpload && (
            <button className="btn-primary" onClick={onUpload}>
              <Upload size={20} />
              {/* Wrap text in span for mobile-only icon display */}
              <span>Upload Media</span>
            </button>
          )}
          
          {/* Refresh button */}
          <button 
            className="btn-secondary" 
            onClick={refreshAlbumPhotos}
            disabled={refreshing}
            title="Refresh photos"
          >
            {refreshing ? (
              <Loader size={20} className="spinning" />
            ) : (
              <RefreshCw size={20} />
            )}
            {/* Wrap text in span for mobile-only icon display */}
            <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
          </button>
          
          {isAlbumOwner() && (
            <button 
              className="btn-delete-album" 
              onClick={() => setShowDeleteConfirm(true)}
              title="Delete Album"
            >
              <Trash2 size={20} />
              {/* Wrap text in span for mobile-only icon display */}
              <span>Delete Album</span>
            </button>
          )}
        </div>
      </header>

      <main className="album-content">
        {loading ? (
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading media...</p>
          </div>
        ) : photos.length === 0 ? (
          <div className="empty-state">
            <Image size={64} className="empty-icon" />
            <h3>No media in this album</h3>
            {canUpload && (
              <button className="btn-primary" onClick={onUpload}>
                <Upload size={20} />
                Upload photos & videos
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="photo-grid">
              {photos.slice((currentPage - 1) * photosPerPage, currentPage * photosPerPage).map((photo, pageIndex) => {
                const index = (currentPage - 1) * photosPerPage + pageIndex; // Actual index in full photos array
              const isVideo = photo.content_type && photo.content_type.startsWith('video/');
              
              // Debug: Log each photo being rendered
              if (index === 0 || isVideo) {
                console.log(`🖼️ Rendering ${isVideo ? 'VIDEO' : 'IMAGE'}: ${photo.original_name} (${photo.content_type})`);
              }
              
              return (
              <div key={photo.id} className="photo-item" onClick={() => openLightbox(index)}>
                {isVideo ? (
                  <div className="video-thumbnail">
                    <img
                      src={photo.b2_thumbnail_name ? 
                        `${getApiUrl()}/api/photos/${photo.id}/thumbnail` : 
                        `${getApiUrl()}/api/photos/${photo.id}/view`
                      }
                      alt={photo.original_name}
                      className="photo-thumbnail"
                      onError={(e) => {
                        // Fallback to main video file if thumbnail fails
                        if (e.target.src.includes('/thumbnail')) {
                          e.target.src = `${getApiUrl()}/api/photos/${photo.id}/view`;
                        }
                      }}
                      loading="lazy"
                      decoding="async"
                    />
                    <div className="video-play-overlay">
                      <Play size={32} fill="white" />
                    </div>
                  </div>
                ) : (
                  <img
                    src={`${getApiUrl()}/api/photos/${photo.id}/thumbnail`}
                    alt={photo.original_name}
                    className="photo-thumbnail"
                    loading="lazy"
                    decoding="async"
                  />
                )}
                {currentCoverPhotoId === photo.id && (
                  <div className="cover-badge">
                    <Star size={16} fill="currentColor" />
                    Cover
                  </div>
                )}
                <div className="photo-overlay">
                  <div className="photo-actions">
                    {isAlbumOwner() && (
                      <button 
                        className={`photo-action set-cover ${currentCoverPhotoId === photo.id ? 'active' : ''}`}
                        onClick={(e) => handleSetCoverPhoto(photo.id, e)}
                        title={currentCoverPhotoId === photo.id ? "Current album cover" : "Set as album cover"}
                      >
                        <Star size={16} fill={currentCoverPhotoId === photo.id ? "currentColor" : "none"} />
                      </button>
                    )}
                    {canDeletePhoto(photo) && (
                      <button 
                        className="photo-action delete-action"
                        onClick={(e) => handleDeletePhoto(photo.id, e)}
                        title="Delete photo"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )})}
            </div>
            
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="pagination-controls">
                <button 
                  className="pagination-btn" 
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <span className="pagination-info">
                  Page {currentPage} of {totalPages} ({photos.length} photos total)
                </span>
                <button 
                  className="pagination-btn" 
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </main>

      {/* Processing Overlay */}
      {refreshing && (
        <div className="processing-overlay">
          <div className="processing-message">
            <Loader size={32} className="spinning" />
            <h3>Refreshing photos...</h3>
            <p>Please wait while we load the latest photos</p>
          </div>
        </div>
      )}

      {/* Desktop Lightbox - rendered as portal to document.body */}
      {selectedPhoto && !isMobile && createPortal(
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox}>
              <X size={24} />
            </button>
            
            <div className="lightbox-actions">
              {isAlbumOwner() && (
                <button 
                  className={`lightbox-action set-cover ${selectedPhoto.id === currentCoverPhotoId ? 'active' : ''}`}
                  onClick={() => handleSetCoverPhoto(selectedPhoto.id, { stopPropagation: () => {} })}
                  title={selectedPhoto.id === currentCoverPhotoId ? "Current album cover" : "Set as album cover"}
                >
                  <Star size={20} fill={selectedPhoto.id === currentCoverPhotoId ? "currentColor" : "none"} />
                </button>
              )}
              {canDeletePhoto(selectedPhoto) && (
                <button 
                  className="lightbox-action delete-action"
                  onClick={() => handleDeletePhoto(selectedPhoto.id, { stopPropagation: () => {} })}
                  title="Delete photo"
                >
                  <Trash2 size={20} />
                </button>
              )}
              <a 
                href={`${getApiUrl()}/api/photos/${selectedPhoto.id}/view`}
                download={selectedPhoto.original_name}
                className="lightbox-action"
                title="Download photo"
              >
                <Download size={20} />
              </a>
            </div>
            
            {photos.length > 1 && (
              <>
                <button className="lightbox-nav lightbox-prev" onClick={() => navigatePhoto('prev')}>
                  <ChevronLeft size={24} />
                </button>
                <button className="lightbox-nav lightbox-next" onClick={() => navigatePhoto('next')}>
                  <ChevronRight size={24} />
                </button>
              </>
            )}
            
            {selectedPhoto.content_type && selectedPhoto.content_type.startsWith('video/') ? (
              <video 
                src={`${getApiUrl()}/api/photos/${selectedPhoto.id}/view`}
                className="lightbox-video"
                controls
                preload="metadata"
                playsInline
                webkit-playsinline="true"
                autoPlay={false}
                muted={false}
              >
                Your browser does not support the video tag.
              </video>
            ) : (
              <img 
                src={`${getApiUrl()}/api/photos/${selectedPhoto.id}/view`}
                alt={selectedPhoto.original_name}
                className="lightbox-image"
              />
            )}
            
            {/* Info toggle button */}
            <button 
              className="lightbox-info-toggle"
              onClick={() => setShowDetails(!showDetails)}
              title={showDetails ? "Hide details" : "Show details"}
            >
              <Info size={20} />
            </button>
            
            {/* Collapsible details */}
            {showDetails && (
              <div className="lightbox-details">
                <div className="lightbox-details-content">
                  <h4>{selectedPhoto.original_name}</h4>
                  <p className="photo-meta-info">
                    {formatFileSize(selectedPhoto.size_bytes)} • 
                    Uploaded {formatDate(selectedPhoto.uploaded_at)}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Delete Album Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="delete-confirm-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Album</h3>
            <p>Are you sure you want to delete "{album.name}"?</p>
            <p className="delete-warning">
              This will permanently delete the album and all {photos.length} items in it. This action cannot be undone.
            </p>
            <div className="delete-confirm-actions">
              <button 
                className="btn-cancel" 
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button 
                className="btn-delete" 
                onClick={handleDeleteAlbum}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete Album'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Photo Viewer - rendered as portal to document.body */}
      {showMobileViewer && isMobile && createPortal(
        <MobilePhotoViewer
          photos={photos}
          currentIndex={currentPhotoIndex}
          onClose={closeLightbox}
          onNavigate={(newIndex) => {
            setCurrentPhotoIndex(newIndex);
            setSelectedPhoto(photos[newIndex]);
          }}
        />,
        document.body
      )}
    </div>
  );
};

export default AlbumViewer;